// Product imagery helpers: route remote images through our cached proxy so we
// never hot-link third-party hosts, plus an on-brand placeholder.

const PROXY_HOSTS = new Set([
  'images-na.ssl-images-amazon.com',
  'm.media-amazon.com',
  'images.unsplash.com',
]);

export const proxiedImage = (url?: string): string => {
  if (!url || !url.trim()) return '';
  const u = url.trim();
  if (u.startsWith('/') || u.startsWith('data:')) return u;
  try {
    const parsed = new URL(u);
    if (PROXY_HOSTS.has(parsed.hostname)) {
      return `/img?u=${encodeURIComponent(u)}`;
    }
  } catch {
    return '';
  }
  return u;
};

export const asinImage = (asin?: string): string =>
  asin && asin.trim()
    ? proxiedImage(`https://images-na.ssl-images-amazon.com/images/P/${asin.trim()}.01._SCLZZZZZZZ_.jpg`)
    : '';

export const productImage = (p: { image?: string; asin?: string }): string => {
  if (p && p.image && p.image.trim()) return proxiedImage(p.image);
  return asinImage(p && p.asin);
};

// True when the product has no usable imagery at all (drives "push to bottom").
export const hasRealImage = (p: { image?: string; asin?: string }): boolean =>
  Boolean((p.image && p.image.trim()) || (p.asin && p.asin.trim()));

// On-brand placeholder: GoodCart mark + "Image coming soon".
export const placeholderImage = (): string => {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='500' height='500' viewBox='0 0 500 500'>` +
    `<rect width='500' height='500' fill='#f2f0ea'/>` +
    `<g fill='none' stroke='#9db392' stroke-width='10' stroke-linecap='round' stroke-linejoin='round'>` +
    `<path d='M170 185 h140 l-18 105 a16 16 0 0 1 -16 13 H184 a16 16 0 0 1 -16 -13 z'/>` +
    `<path d='M205 185 v-18 a25 25 0 0 1 50 0 v18'/></g>` +
    `<circle cx='205' cy='330' r='11' fill='#9db392'/>` +
    `<circle cx='255' cy='330' r='11' fill='#9db392'/>` +
    `<text x='250' y='400' text-anchor='middle' font-family='Georgia, serif' font-size='30' fill='#5c6f52' font-style='italic'>Image coming soon</text>` +
    `<text x='250' y='438' text-anchor='middle' font-family='Georgia, serif' font-size='17' letter-spacing='4' fill='#8a8577'>GOODCART</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Attach to an <img onError> to swap in the placeholder exactly once.
export const onImgError = () => (e: any) => {
  const t = e.currentTarget as HTMLImageElement;
  t.onerror = null;
  t.src = placeholderImage();
};
